'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  type AgentState,
  type ReceivedChatMessage,
  useRoomContext,
  useVoiceAssistant,
} from '@livekit/components-react';
import { toastAlert } from '@/components/alert-toast';
import { ProfessionalClinicalNote } from '@/components/clinical-note/professional-clinical-note';
import { TraditionalClinicalNote } from '@/components/clinical-note/traditional-clinical-note';
import { TurnEmotionPanel } from '@/components/emotion/turn-emotion-panel';
import { AgentControlBar } from '@/components/livekit/agent-control-bar/agent-control-bar';
import { ChatEntry } from '@/components/livekit/chat/chat-entry';
import { ChatMessageView } from '@/components/livekit/chat/chat-message-view';
import { MediaTiles } from '@/components/livekit/media-tiles';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import useChatAndTranscription from '@/hooks/useChatAndTranscription';
import { useDebugMode } from '@/hooks/useDebug';
import { useStreamClinicalNote } from '@/hooks/useStreamClinicalNote';
import { useTurnEmotion } from '@/hooks/useTurnEmotion';
import type { AppConfig } from '@/lib/types';
import { cn, messagesToTranscript } from '@/lib/utils';

function isAgentAvailable(agentState: AgentState) {
  return agentState == 'listening' || agentState == 'thinking' || agentState == 'speaking';
}

interface SessionViewWithEmotionPanelProps {
  appConfig: AppConfig;
  disabled: boolean;
  sessionStarted: boolean;
  /** When true, free-chat demo: always show 回顾心声. When false/undefined, hide 回顾心声 if patient is logged in (TOD). */
  isFreeChatDemo?: boolean;
}

/**
 * Session view with an optional in-memory Turn Emotion developer panel.
 * The panel is available only in Free Chat and never persists model output.
 */
export const SessionViewWithEmotionPanel = ({
  appConfig,
  disabled,
  sessionStarted,
  isFreeChatDemo,
  ref,
}: React.ComponentProps<'div'> & SessionViewWithEmotionPanelProps) => {
  const { state: agentState } = useVoiceAssistant();
  const [chatOpen, setChatOpen] = useState(false);
  const [summaryCanvasOpen, setSummaryCanvasOpen] = useState(false);
  const [emotionPanelOpen, setEmotionPanelOpen] = useState(false);
  const [desktopEmotionPanelVisible, setDesktopEmotionPanelVisible] = useState(false);
  // When not free-chat demo: true if patient_session_info is in sessionStorage (TOD patient flow → hide 回顾心声)
  const [hasPatientSessionInStorage, setHasPatientSessionInStorage] = useState(false);
  // Note style toggle state (modern vs traditional)
  const [noteStyle, setNoteStyle] = useState<'modern' | 'traditional'>('modern');
  const room = useRoomContext();
  const turnEmotionUiEnabled =
    !!isFreeChatDemo && process.env.NEXT_PUBLIC_TURN_EMOTION_UI_ENABLED === 'true';

  // Detect patient session so we hide 回顾心声 in TOD patient flow
  useEffect(() => {
    if (typeof window === 'undefined' || isFreeChatDemo) return;
    setHasPatientSessionInStorage(!!sessionStorage.getItem('patient_session_info'));
  }, [isFreeChatDemo]);

  const showSummaryButton = isFreeChatDemo || !hasPatientSessionInStorage;

  // Load user preference from sessionStorage
  useEffect(() => {
    const savedStyle = sessionStorage.getItem('clinical-note-style');
    if (savedStyle === 'traditional' || savedStyle === 'modern') {
      setNoteStyle(savedStyle);
    }
  }, []);

  // Save preference to sessionStorage
  const handleStyleChange = (style: 'modern' | 'traditional') => {
    setNoteStyle(style);
    sessionStorage.setItem('clinical-note-style', style);
  };

  // Detect screen size to toggle appropriate panel
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    // 确保在客户端运行
    if (typeof window === 'undefined') return;

    const checkScreenSize = () => {
      try {
        setIsMobile(window.innerWidth < 1024); // lg breakpoint
        // setIsCompactHeight(window.innerHeight < 700); // 临时禁用
      } catch (error) {
        console.error('Error checking screen size:', error);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Use room.name directly as sessionId (more reliable than state)
  const currentSessionId = room?.name || '';

  // Keep state setter for backward compatibility (if needed elsewhere)
  useEffect(() => {
    if (room && room.name) {
      console.log('📌 [Session] Session ID set:', room.name);
    }
  }, [room]);

  const { messages, send } = useChatAndTranscription(currentSessionId);

  // Streaming clinical note state
  const {
    originalNote,
    draftNote,
    isStreaming: isNoteStreaming,
    streamingProgress,
    streamClinicalNote,
    updateDraftNote,
  } = useStreamClinicalNote({
    sessionId: currentSessionId, // Pass sessionId for auto-saving
  });

  useDebugMode({
    enabled: process.env.NODE_ENV !== 'production',
  });

  const { latest: latestTurnEmotion, history: turnEmotionHistory } = useTurnEmotion(
    turnEmotionUiEnabled && sessionStarted
  );

  const handleSendMessage = useCallback(
    async (message: string) => {
      await send(message);
    },
    [send]
  );

  const handleSummaryClick = useCallback(() => {
    // Use LiveKit room name as session ID so backend matches stored events
    const sessionId =
      room?.name || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    console.log(`📝 [handleSummaryClick] Generating clinical note for session: ${sessionId}`);
    console.log(`📊 [handleSummaryClick] Frontend has ${messages.length} messages`);

    setSummaryCanvasOpen(true);

    // ✅ NEW APPROACH: Send sessionId, backend will fetch complete transcript from database
    // This avoids issues with incomplete frontend messages due to streaming delays
    streamClinicalNote({
      sessionId,
      sessionMetadata: { sessionId },
    });
  }, [room?.name, streamClinicalNote, messages.length]);

  // Handle session timeout
  useEffect(() => {
    if (sessionStarted) {
      const timeout = setTimeout(() => {
        if (!isAgentAvailable(agentState)) {
          const reason =
            agentState === 'connecting'
              ? 'Agent did not join the room. '
              : 'Agent connected but did not complete initializing. ';

          toastAlert({
            title: 'Session ended',
            description: (
              <p className="w-full">
                {reason}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://docs.livekit.io/agents/start/voice-ai/"
                  className="whitespace-nowrap underline"
                >
                  See quickstart guide
                </a>
                .
              </p>
            ),
          });
          room.disconnect();
        }
      }, 20_000);

      return () => clearTimeout(timeout);
    }
  }, [agentState, sessionStarted, room]);

  // MediaTiles will handle all video tracks automatically

  const { supportsChatInput, supportsVideoInput, supportsScreenShare } = appConfig;
  const capabilities = {
    supportsChatInput,
    supportsVideoInput,
    supportsScreenShare: false, // 隐藏投屏分享开关
  };

  return (
    <>
      <section
        ref={ref}
        inert={disabled}
        className={cn(
          'relative h-screen overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50/30',
          'transition-opacity duration-300',
          // Desktop: Flex layout with persistent panel
          'lg:flex',
          // Mobile: Single column, overlay emotion panel
          'flex-col lg:flex-row',
          !sessionStarted && 'opacity-0'
        )}
      >
        {/* Main Content Area - Left Side */}
        <div className="flex h-full min-w-0 flex-1 flex-col">
          {/* Chat Message View */}
          <div className="relative flex-1 overflow-hidden">
            <ChatMessageView
              className={cn(
                'mx-auto h-full w-full max-w-4xl px-6 pt-32 pb-40',
                'overflow-y-auto transition-all duration-300 ease-out',
                chatOpen ? 'translate-y-0 opacity-100 delay-200' : 'translate-y-20 opacity-0'
              )}
            >
              <div className="space-y-4">
                <AnimatePresence>
                  {messages.map((message: ReceivedChatMessage, index: number) => (
                    <motion.div
                      key={message.id || `msg-${message.timestamp}-${index}`}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 1, height: 'auto', translateY: 0.001 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      <ChatEntry
                        hideName
                        key={message.id || `msg-${message.timestamp}-${index}`}
                        entry={message}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Removed status bar - emotion indicator moved to top-right */}
              </div>
            </ChatMessageView>

            {/* Background Gradient */}
            <div className="pointer-events-none absolute top-0 right-0 left-0 h-32 bg-gradient-to-b from-slate-50/80 to-transparent" />

            {/* Media Tiles - Includes Agent (with voice animation) and User Video */}
            <MediaTiles chatOpen={chatOpen} />

            {/* Control Bar */}
            <div className="absolute right-0 bottom-0 left-0 z-50 bg-gradient-to-t from-slate-50/90 via-slate-50/50 to-transparent px-6 pt-4 pb-6 backdrop-blur-sm">
              <motion.div
                key="control-bar"
                initial={{ opacity: 0, translateY: '100%' }}
                animate={{
                  opacity: sessionStarted ? 1 : 0,
                  translateY: sessionStarted ? '0%' : '100%',
                }}
                transition={{ duration: 0.3, delay: sessionStarted ? 0.5 : 0, ease: 'easeOut' }}
              >
                <div className="relative z-10 mx-auto w-full max-w-4xl">
                  {/* Pre-connect Buffer Message */}
                  {appConfig.isPreConnectBufferEnabled && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: sessionStarted && messages.length === 0 ? 1 : 0,
                        transition: {
                          ease: 'easeIn',
                          delay: messages.length > 0 ? 0 : 0.8,
                          duration: messages.length > 0 ? 0.2 : 0.5,
                        },
                      }}
                      aria-hidden={messages.length > 0}
                      className={cn(
                        'absolute inset-x-0 -top-16 text-center',
                        sessionStarted && messages.length === 0 && 'pointer-events-none'
                      )}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <p className="animate-text-shimmer inline-block !bg-clip-text text-sm font-semibold text-transparent">
                          心镜正在静静倾听...
                        </p>
                        {turnEmotionUiEnabled && (
                          <div className="flex items-center gap-2 rounded-full border border-emerald-200/50 bg-gradient-to-r from-emerald-50 to-blue-50 px-3 py-1 backdrop-blur-sm">
                            <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                            <span className="text-xs text-emerald-700">Turn 情绪辅助已启用</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* Enhanced Control Bar */}
                  <AgentControlBar
                    capabilities={capabilities}
                    onChatOpenChange={setChatOpen}
                    onSendMessage={handleSendMessage}
                    onSummaryClick={showSummaryButton ? handleSummaryClick : undefined}
                    onEmotionPanelToggle={
                      turnEmotionUiEnabled
                        ? () => {
                            // 移动端切换 Sheet，桌面端切换固定面板（仅 free-chat 或非患者时显示）
                            if (isMobile) {
                              setEmotionPanelOpen((prev) => !prev);
                            } else {
                              setDesktopEmotionPanelVisible((prev) => !prev);
                            }
                          }
                        : undefined
                    }
                    emotionPanelVisible={
                      showSummaryButton
                        ? isMobile
                          ? emotionPanelOpen
                          : desktopEmotionPanelVisible
                        : false
                    }
                    messages={messages}
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Desktop: Background overlay when panel is open */}
        <AnimatePresence>
          {turnEmotionUiEnabled && desktopEmotionPanelVisible && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setDesktopEmotionPanelVisible(false)}
              className="fixed inset-0 z-40 hidden bg-slate-900/10 backdrop-blur-[2px] lg:block"
            />
          )}
        </AnimatePresence>

        {/* Free Chat Turn Emotion panel - desktop overlay */}
        {turnEmotionUiEnabled && (
          <motion.div
            initial={{ opacity: 0, x: 400 }}
            animate={{
              opacity: desktopEmotionPanelVisible ? 1 : 0,
              x: desktopEmotionPanelVisible ? 0 : 400,
            }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className={cn(
              // 改为绝对定位的 Overlay
              'fixed top-0 right-0 z-50',
              'hidden lg:block',
              'h-screen',
              // 添加阴影
              'shadow-2xl',
              // 当不可见时完全隐藏
              !desktopEmotionPanelVisible && 'pointer-events-none'
            )}
          >
            <TurnEmotionPanel
              latest={latestTurnEmotion}
              history={turnEmotionHistory}
              className="h-full"
            />
          </motion.div>
        )}
      </section>

      {/* Clinical Note Sheet */}
      <Sheet open={summaryCanvasOpen} onOpenChange={setSummaryCanvasOpen}>
        <SheetContent
          side="right"
          className="flex w-full flex-col bg-gradient-to-br from-slate-50 to-blue-50 p-0 sm:max-w-5xl"
        >
          <SheetHeader className="flex-shrink-0 space-y-3 border-b border-slate-200/50 p-4">
            <div className="flex items-center justify-between">
              <SheetTitle>临床记录</SheetTitle>
              {/* Style Toggle Buttons */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">样式：</span>
                <div className="flex gap-1 rounded-md bg-slate-100 p-1">
                  <button
                    onClick={() => handleStyleChange('modern')}
                    className={`rounded px-3 py-1.5 text-sm font-medium transition-all ${
                      noteStyle === 'modern'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    🎨 现代
                  </button>
                  <button
                    onClick={() => handleStyleChange('traditional')}
                    className={`rounded px-3 py-1.5 text-sm font-medium transition-all ${
                      noteStyle === 'traditional'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    📋 传统
                  </button>
                </div>
              </div>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-auto">
            {noteStyle === 'modern' ? (
              <ProfessionalClinicalNote
                content={draftNote || ''}
                isStreaming={isNoteStreaming}
                streamingProgress={streamingProgress}
                onContentChange={updateDraftNote}
                sessionId={currentSessionId}
                className="h-full"
              />
            ) : (
              <div className="p-6">
                <TraditionalClinicalNote
                  sessionId={currentSessionId}
                  patientName="匿名患者"
                  age=""
                  gender=""
                  department="心理科"
                  phone=""
                  recordNumber={currentSessionId}
                  doctorName=""
                  content={draftNote || ''}
                  className="origin-top scale-90"
                />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Mobile Emotion Panel Sheet */}
      <Sheet open={turnEmotionUiEnabled && emotionPanelOpen} onOpenChange={setEmotionPanelOpen}>
        <SheetContent
          side="right"
          className="w-full bg-gradient-to-b from-slate-50 to-white p-0 sm:max-w-sm"
        >
          <TurnEmotionPanel
            latest={latestTurnEmotion}
            history={turnEmotionHistory}
            className="h-full w-full"
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default SessionViewWithEmotionPanel;
